import {
    Sidebar,
    SidebarContent,
    SidebarFooter,
    SidebarHeader,
    SidebarMenu,
    SidebarMenuButton,
    SidebarMenuItem,
    useSidebar,
} from '@/components/ui/sidebar';
import { Home, List, ReceiptText, Handshake, UserRoundCog, FileText, ClipboardList, BookOpen, Sparkles, Gauge, CircleUser, Network, LogOut, PanelLeft, PanelLeftClose, Settings, type LucideIcon } from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { type RootState } from '@/lib/redux/store';
import api from '@/lib/axiosInstance';
import { useDispatch, useSelector } from 'react-redux';
import { logout } from '@/lib/redux/authSlice';
import { 
    DropdownMenu,
    DropdownMenuTrigger, 
    DropdownMenuContent, 
    DropdownMenuItem, 
    DropdownMenuLabel, 
    DropdownMenuSeparator 
} from '@/components/ui/dropdown-menu';
import { toast } from 'sonner';
import { Separator } from './ui/separator';
import { useBilling } from './billing/billingContext';

interface NavItem {
    label: string;
    path: string;
    icon: LucideIcon;
    /** Only for Xpillar's own agents. */
    internal?: boolean;
}

const MAIN: NavItem[] = [
    { label: 'Dashboard', path: '/dashboard', icon: Home },
    { label: 'Agent Hand Book', path: '/agent-handbook?tab=manual', icon: BookOpen },
    { label: 'Listings', path: '/listings?page=1&length=10&type=active', icon: List },
    { label: 'Co-share listings', path: '/co-listings', icon: Handshake, internal: true },
    { label: 'Claims', path: '/claims', icon: ReceiptText, internal: true },
    { label: 'Documents', path: '/documents', icon: FileText },
    { label: 'Leads', path: '/leads', icon: UserRoundCog },
];

/** The document forms, for internal agents. */
const FORMS: NavItem[] = [
    { label: 'Claim Form', path: '/documents/claims', icon: FileText },
    { label: 'Co Agency Agreement Form', path: '/documents/co-agency-agreement', icon: FileText },
    { label: 'Exclusive Authorize To Sell', path: '/documents/exclusive-authorize-to-sell', icon: FileText },
    { label: 'Exclusive Authorize To Rent', path: '/documents/exclusive-authorize-to-rent', icon: FileText },
    { label: 'Letter Of Undertaking', path: '/documents/letter-of-undertaking', icon: ClipboardList },
    { label: 'Agreement To Purchase', path: '/documents/agreement-to-purchase', icon: ClipboardList },
    { label: 'Agreement To Rent', path: '/documents/agreement-to-rent', icon: ClipboardList },
    { label: 'Authorization To Sell', path: '/documents/authorization-to-sell', icon: ClipboardList },
    { label: 'Authorization To Let', path: '/documents/authorization-to-let', icon: ClipboardList },
];

const SideBar = () => {
    const agent = useSelector((state: RootState) => state.auth.agent);
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { setOpenMobile, state, toggleSidebar, isMobile } = useSidebar();
    const location = useLocation();
    const { subscription, openPricing, openUsage } = useBilling();

    const logoutUser = async () => {
        try {
            const response = await api.get(`/agents/logout`);
            if (!response.data.status) {
                throw new Error(response.data.error);
            }
        } catch (err: any) {
            const error = err.response ? err.response.data.error : err.message;
            toast.error(error);
        } finally {
            dispatch(logout());
            navigate('/login');
        }
    };

    const collapsed = state === 'collapsed' && !isMobile;
    const go = (path: string) => {
        setOpenMobile(false);
        navigate(path);
    };
    const isActive = (path: string) => location.pathname === path.split('?')[0];

    const item = ({ label, path, icon: Icon }: NavItem) => (
        <SidebarMenuItem key={path}>
            <SidebarMenuButton
                tooltip={label}
                isActive={isActive(path)}
                className="h-10 gap-3 px-3 text-black cursor-pointer [&>svg]:size-5"
                onClick={() => go(path)}>
                <Icon />
                <span>{label}</span>
            </SidebarMenuButton>
        </SidebarMenuItem>
    );

    return (
        <Sidebar 
        side='left'
        variant='sidebar'
        collapsible='icon'>
            <SidebarHeader className="py-3">
                {collapsed ? (
                    <button
                        type="button"
                        onClick={toggleSidebar}
                        aria-label="Expand sidebar"
                        title="Expand sidebar (Ctrl+B)"
                        className="mx-auto flex h-8 w-8 cursor-pointer items-center justify-center rounded-md text-gray-600 transition-colors hover:bg-gray-100">
                        <PanelLeft className="h-4 w-4" />
                    </button>
                ) : (
                    <div className="flex items-center justify-between">
                        <span className="text-xl font-bold ml-3 whitespace-nowrap">Hero | Xpillar</span>
                        <button
                            type="button"
                            onClick={toggleSidebar}
                            aria-label="Collapse sidebar"
                            title="Collapse sidebar (Ctrl+B)"
                            className="hidden h-8 w-8 cursor-pointer items-center justify-center rounded-md text-gray-600 transition-colors hover:bg-gray-100 md:flex">
                            <PanelLeftClose className="h-4 w-4" />
                        </button>
                    </div>
                )}
            </SidebarHeader>

            <SidebarContent className="no-scrollbar">
                <SidebarMenu className="gap-1 px-2 py-2">
                    {MAIN.filter((x) => !x.internal || agent.isInternal).map(item)}
                </SidebarMenu>
                {agent.isInternal === 1 && (
                    <>
                        <Separator />
                        <SidebarMenu className="gap-1 px-2 py-2">
                            {FORMS.map(item)}
                        </SidebarMenu>
                    </>
                )}
            </SidebarContent>
            <SidebarFooter className={collapsed ? 'px-2 py-3' : 'px-4 py-4'}>
                <DropdownMenu>
                    <DropdownMenuTrigger className={`flex items-center text-left cursor-pointer ${collapsed ? 'justify-center' : ''}`} title={collapsed ? `${agent.firstName} ${agent.lastName}` : undefined}>
                        <Avatar className={collapsed ? 'h-8 w-8' : 'mr-3'}>
                            <AvatarImage src={`${import.meta.env.VITE_AWS_S3}/${agent.avatar}`} />
                            <AvatarFallback className="bg-gray-200 text-gray-500 text-sm">
                            { agent?.firstName?.[0]?.toUpperCase() ?? 'N' }
                            { agent?.lastName?.[0]?.toUpperCase() ?? '' }
                            </AvatarFallback>
                        </Avatar>
                        <span className={`flex flex-col leading-tight ${collapsed ? 'hidden' : ''}`}>
                            <span className="text-black">{agent.firstName} {agent.lastName}</span>
                            {subscription !== undefined && (
                                <span className="text-xs text-gray-500">{subscription?.plan?.name ?? 'No plan'}</span>
                            )}
                        </span>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent className="w-56" side={collapsed ? 'right' : 'bottom'} align={collapsed ? 'end' : 'start'}>
                        <DropdownMenuLabel>My Account</DropdownMenuLabel>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem 
                        className='cursor-pointer'
                        onClick={() => {
                            setOpenMobile(false);
                            navigate('/profile')
                        }}>
                            <CircleUser className="h-4 w-4" />
                            Profile
                        </DropdownMenuItem>
                        <DropdownMenuItem 
                        className='cursor-pointer'
                        onClick={() => {
                            setOpenMobile(false);
                            openUsage()
                        }}>
                            <Gauge className="h-4 w-4" />
                            Usage
                        </DropdownMenuItem>
                        <DropdownMenuItem 
                        className='cursor-pointer'
                        onClick={() => {
                            setOpenMobile(false);
                            openPricing('plans')
                        }}>
                            <Sparkles className="h-4 w-4" />
                            Pricing
                        </DropdownMenuItem>
                        <DropdownMenuItem 
                        className={`cursor-pointer ${!agent.isInternal && 'hidden'}`}
                        onClick={() => {
                            setOpenMobile(false);
                            navigate('/tree')
                        }}>
                            <Network className="h-4 w-4" />
                            Generation Tree
                        </DropdownMenuItem>
                        <DropdownMenuItem 
                        className='cursor-pointer'
                        onClick={() => {
                            setOpenMobile(false);
                            navigate('/settings')
                        }}>
                            <Settings className="h-4 w-4" />
                            Settings
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem className='cursor-pointer' onClick={logoutUser}>
                            <LogOut className="h-4 w-4" />
                            Logout
                        </DropdownMenuItem>
                    </DropdownMenuContent>
                </DropdownMenu>
                {!collapsed && <span className="text-sm text-center text-gray-500">© {new Date().getFullYear()} Xpillar</span>}
            </SidebarFooter>
        </Sidebar>
    );
};

export default SideBar;
